import { useCallback, useEffect, useMemo, useState } from "react";
import { PRODUCTS } from "@/data/products";

const STORAGE_KEY = "rimbara-maju-cart";

function loadCart() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (i) => i && typeof i.id === "string" && Number.isInteger(i.qty) && i.qty > 0
      )
      .filter((i) => PRODUCTS.some((p) => p.id === i.id && p.visible))
      .map((i) => ({ id: i.id, qty: i.qty }));
  } catch {
    return [];
  }
}

export function useCart() {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // localStorage unavailable — cart works for this session only
    }
  }, [items]);

  const add = useCallback((id) => {
    setItems((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) {
        return prev.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { id, qty: 1 }];
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) => {
      const q = Math.floor(Number(qty));
      if (!Number.isFinite(q) || q <= 0) {
        return prev.filter((i) => i.id !== id);
      }
      return prev.map((i) => (i.id === id ? { ...i, qty: q } : i));
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const lines = useMemo(
    () =>
      items
        .map((i) => {
          const product = PRODUCTS.find((p) => p.id === i.id);
          return product ? { ...product, qty: i.qty } : null;
        })
        .filter(Boolean),
    [items]
  );

  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);
  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + l.qty * l.price, 0),
    [lines]
  );

  return { items, lines, add, setQty, remove, clear, count, subtotal };
}
